import React from 'react';
import ChildSwitcher from './ChildSwitcher';
import { useMyChildren } from '../hooks/useMyChildren';

function downloadCSV(filename, headerRow, dataRows) {
  const rows = [headerRow, ...dataRows];
  const csv = rows
    .map(row => row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function ChildAttendance() {
  const { children, selectedChild, setSelectedChild, loading } = useMyChildren();

  if (loading) {
    return <div className="bg-white p-8 rounded shadow text-center text-gray-500">Loading…</div>;
  }

  if (children.length === 0) {
    return (
      <div className="bg-white p-8 rounded shadow text-center text-gray-400">
        <i className="fas fa-inbox text-4xl mb-3"></i>
        <p>No children linked to your account.</p>
      </div>
    );
  }

  const records = selectedChild?.attendance_records || [];

  const byMonth = {};
  records.forEach(r => {
    const m = r.date.substring(0, 7);
    if (!byMonth[m]) byMonth[m] = [];
    byMonth[m].push(r);
  });
  const months = Object.keys(byMonth).sort().reverse();

  const handleDownload = (monthKey) => {
    const rows = byMonth[monthKey]
      .slice()
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(r => [r.date, r.status]);
    downloadCSV(
      `attendance_${selectedChild.first_name}_${monthKey}.csv`,
      ['Date', 'Status'],
      rows,
    );
  };

  const handleDownloadAll = () => {
    const rows = records
      .slice()
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(r => [r.date, r.status]);
    downloadCSV(
      `attendance_${selectedChild.first_name}_all.csv`,
      ['Date', 'Status'],
      rows,
    );
  };

  return (
    <div>
      <h2 className="text-xl font-bold mb-4 text-gray-800">
        <i className="fas fa-calendar-check text-blue-500 mr-2"></i>Attendance Report
      </h2>

      <ChildSwitcher
        children={children}
        selectedChild={selectedChild}
        onSelect={setSelectedChild}
      />

      {months.length === 0 ? (
        <div className="bg-white p-8 rounded shadow text-center text-gray-400">
          <i className="fas fa-calendar-times text-4xl mb-3"></i>
          <p>No attendance records yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-white p-3 sm:p-4 rounded shadow flex flex-wrap gap-3 items-center justify-between">
            <p className="text-sm text-gray-600">
              <i className="fas fa-info-circle mr-1"></i>
              {records.length} records across {months.length} months
            </p>
            <button
              onClick={handleDownloadAll}
              className="px-4 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700"
            >
              <i className="fas fa-download mr-1"></i> Download All
            </button>
          </div>

          {months.map(monthKey => {
            const monthRecords = byMonth[monthKey];
            const present = monthRecords.filter(r => r.status === 'Present').length;
            const absent = monthRecords.filter(r => r.status === 'Absent').length;
            const late = monthRecords.filter(r => r.status === 'Late').length;
            const total = monthRecords.length;
            const rate = total ? Math.round((present / total) * 100) : 0;
            const label = new Date(monthKey + '-01').toLocaleDateString('en', {
              month: 'long',
              year: 'numeric',
            });

            return (
              <div key={monthKey} className="bg-white rounded shadow overflow-hidden">
                <div className="flex items-center justify-between p-3 sm:p-4 border-b bg-gray-50 flex-wrap gap-2">
                  <h3 className="font-semibold text-gray-800">{label}</h3>
                  <button
                    onClick={() => handleDownload(monthKey)}
                    className="px-3 py-1 text-xs bg-green-100 text-green-800 rounded hover:bg-green-200"
                  >
                    <i className="fas fa-download mr-1"></i> Download
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 p-3 sm:p-4">
                  <div className="text-center bg-green-50 rounded p-2">
                    <p className="text-xs text-green-600 font-semibold">Present</p>
                    <p className="text-lg font-bold text-green-700">{present}</p>
                  </div>
                  <div className="text-center bg-red-50 rounded p-2">
                    <p className="text-xs text-red-600 font-semibold">Absent</p>
                    <p className="text-lg font-bold text-red-700">{absent}</p>
                  </div>
                  <div className="text-center bg-yellow-50 rounded p-2">
                    <p className="text-xs text-yellow-600 font-semibold">Late</p>
                    <p className="text-lg font-bold text-yellow-700">{late}</p>
                  </div>
                  <div className="text-center bg-gray-50 rounded p-2">
                    <p className="text-xs text-gray-600 font-semibold">Total</p>
                    <p className="text-lg font-bold text-gray-700">{total}</p>
                  </div>
                  <div className="text-center bg-blue-50 rounded p-2">
                    <p className="text-xs text-blue-600 font-semibold">Rate</p>
                    <p className="text-lg font-bold text-blue-700">{rate}%</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}